import axios from 'axios';
import jwtDecode from 'jwt-decode';
import config from '../../config';

export const AUTH_SESSION_KEY = 'vitale_user';
export const AUTH_SESSION_SSO_KEY = 'ssoToken';
export const SESSION_REQUIRED = 'vitale-session-required';
export const SESSION_RENEWED = 'vitale-session-renewed';
export const SESSION_CHANGED = 'vitale-session-changed';
const authClient = axios.create({ baseURL: config.API_URL_V2 });
let renewal: Promise<any> | undefined;

export class SessionRequiredError extends Error {
    readonly response: { status: number; data: { code: number; message: string } };
    constructor() {
        super('Your session needs to be renewed. Sign in again to save your changes.');
        this.response = { status: 401, data: { code: 401, message: this.message } };
    }
}
export const readSession = () => {
    try { return JSON.parse(sessionStorage.getItem(AUTH_SESSION_KEY) || 'null'); }
    catch { return null; }
};
export const accessToken = (session: any) => session?.tokens?.access?.token || session?.token;
export const tokenValid = (token?: string, remainingSeconds = 0) => {
    if (!token) return false;
    try {
        const decoded = jwtDecode<{ exp?: number }>(token);
        return decoded.exp === undefined || (Number.isFinite(decoded.exp) && decoded.exp > Date.now() / 1000 + remainingSeconds);
    } catch { return false; }
};
export const isSsoSession = (session: any) => session?.authMethod === 'sso'
    || (!!sessionStorage.getItem(AUTH_SESSION_SSO_KEY) && accessToken(session) === sessionStorage.getItem(AUTH_SESSION_SSO_KEY));
export const requireSignIn = () => window.dispatchEvent(new Event(SESSION_REQUIRED));
const emailOf = (session: any) => session?.user?.email || session?.email;

const storeRenewedSession = (original: any, update: any, sso = false) => {
    const current = readSession();
    // A late refresh must never restore a logged-out or replaced account.
    if (!current || accessToken(current) !== accessToken(original)
        || current?.tokens?.refresh?.token !== original?.tokens?.refresh?.token) {
        throw new Error('The signed-in session changed. Please retry.');
    }
    const session = { ...current, ...update };
    if (sso) sessionStorage.setItem(AUTH_SESSION_SSO_KEY, session.tokens.access.token);
    sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
    window.dispatchEvent(new Event(SESSION_RENEWED));
    return session;
};

const microsoftAccount = async (session: any) => {
    const { msalInstance } = await import('../../msalConfig');
    await msalInstance.initialize();
    const accounts = msalInstance.getAllAccounts();
    const email = emailOf(session)?.toLowerCase();
    const account = accounts.find(item => session.ssoAccountId && item.homeAccountId === session.ssoAccountId)
        || accounts.find(item => email && item.username.toLowerCase() === email);
    return { msalInstance, account };
};
const needsInteraction = (error: any) => error instanceof SessionRequiredError
    || error?.name === 'InteractionRequiredAuthError'
    || error?.response?.status === 401 || error?.response?.status === 400
    || ['interaction_required', 'consent_required', 'login_required', 'no_account_error'].includes(error?.errorCode);

export const ensureSession = async (force = false): Promise<any> => {
    const session = readSession();
    if (!session) throw new SessionRequiredError();
    if (renewal) return renewal;
    if (!force && tokenValid(accessToken(session), 60)) return session;
    renewal = (async () => {
        try {
            if (isSsoSession(session)) {
                const { msalInstance, account } = await microsoftAccount(session);
                if (!account) throw new SessionRequiredError();
                const response = await msalInstance.acquireTokenSilent({ account, scopes: ['User.Read'], forceRefresh: true });
                if (!response.idToken || !tokenValid(response.idToken)) throw new SessionRequiredError();
                const expires = jwtDecode<{ exp?: number }>(response.idToken).exp;
                return storeRenewedSession(session, { authMethod: 'sso', ssoAccountId: account.homeAccountId,
                    tokens: { ...session.tokens, access: { token: response.idToken, expires: expires
                        ? new Date(expires * 1000).toISOString() : undefined } } }, true);
            }
            const refreshToken = session?.tokens?.refresh?.token;
            if (!refreshToken) throw new SessionRequiredError();
            const response = await authClient.post('/auth/refresh-tokens', { refreshToken }, { headers: { Authorization: '' } });
            if (!response.data?.access?.token || !response.data?.refresh?.token) throw new SessionRequiredError();
            return storeRenewedSession(session, { tokens: response.data });
        } catch (error) {
            if (needsInteraction(error)) { requireSignIn(); throw new SessionRequiredError(); }
            throw error;
        }
    })();
    try { return await renewal; } finally { renewal = undefined; }
};

export const signInAgain = async (password?: string) => {
    const session = readSession();
    if (!session) throw new SessionRequiredError();
    if (isSsoSession(session)) {
        const { msalInstance, account } = await microsoftAccount(session);
        const response = await msalInstance.acquireTokenPopup({ scopes: ['User.Read'], account,
            loginHint: emailOf(session), prompt: 'select_account' });
        const expectedEmail = emailOf(session)?.toLowerCase();
        const claims = response.idTokenClaims as { preferred_username?: string } | undefined;
        const actualEmail = claims?.preferred_username?.toLowerCase()
            || response.account?.username.toLowerCase();
        if (!expectedEmail || actualEmail !== expectedEmail) throw new Error('Please sign in with the same account to keep editing this page.');
        if (!response.idToken || !tokenValid(response.idToken)) throw new SessionRequiredError();
        msalInstance.setActiveAccount(response.account);
        return storeRenewedSession(session, { authMethod: 'sso', ssoAccountId: response.account?.homeAccountId,
            tokens: { ...session.tokens, access: { token: response.idToken } } }, true);
    }
    const response = await authClient.post('/auth/login', { email: emailOf(session), password }, { headers: { Authorization: '' } });
    if (!response.data?.tokens?.access?.token || response.data?.user?.email?.toLowerCase() !== emailOf(session)?.toLowerCase()) {
        throw new SessionRequiredError();
    }
    return storeRenewedSession(session, response.data);
};
